/**
 * UserEventsSSEManager - connection management for the per-user notification stream
 * (`/message/sse/users/me`).
 *
 * Mirrors SSEManager, with these deliberate differences:
 *  - it connects on a valid session cookie alone - no account, extended info, closed account
 *    or `app.loaded` checks. It additionally waits for `app.isServicesReady`, which is not an
 *    auth condition but the point at which `GlobalService.amsService` exists to open the stream;
 *  - the stream is scoped to the user, so there is nothing to reconnect on when the account changes.
 */

import { Store } from 'redux';

import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { RootState } from '../../store';
import {
  userSSEConnectionClosed,
  userSSEConnectionError,
  userSSEConnectionStarted,
  userSSEEventReceived,
} from '../../store/ams';
import { hasValidCookie } from '../../store/selectors';
import GlobalService from '../Global.service';
import { LoggerService } from '../logger/Logger.service';

import { setUserEventsLastEvent } from './last-event-storage';
import { isAccountEvent, UserSSEEvent } from './sse-event-types';

const logger = LoggerService.getLogger();

const RECONNECT_DELAY_MS = PredefinedMilisecondsTypeEnum.fiveSeconds;
const MAX_RECONNECT_ATTEMPTS = 5;
/**
 * How long an open stream has to survive before it counts as healthy. A server that accepts the
 * stream and drops it straight away must still spend the attempt budget, otherwise refreshing it
 * on every open would bring back the endless reconnect loop.
 */
const HEALTHY_STREAM_MS = PredefinedMilisecondsTypeEnum.thirtySeconds;

export class UserEventsSSEManager {
  private static instance: UserEventsSSEManager | null = null;
  private cleanupFn: (() => void) | null = null;
  private isConnected: boolean = false;
  private isIntentionalDisconnect: boolean = false;
  private store: Store | null = null;
  private unsubscribeFromStore: (() => void) | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts: number = 0;
  private hasGivenUp: boolean = false;
  private streamOpenedAt: number | null = null;

  // eslint-disable-next-line no-useless-constructor
  private constructor() {
    // to use the existing instance or create a new one please call UserEventsSSEManager.getInstance()
  }

  public static getInstance(): UserEventsSSEManager {
    UserEventsSSEManager.instance ??= new UserEventsSSEManager();
    return UserEventsSSEManager.instance;
  }

  /**
   * Set Redux store for dispatching actions
   * Should be called once during app initialization
   */
  public setStore(store: Store): void {
    this.store = store;
    logger.info('[UserEventsSSEManager] Redux store initialized');

    if (this.unsubscribeFromStore) {
      this.unsubscribeFromStore();
    }

    this.unsubscribeFromStore = this.store.subscribe(() => this.syncWithStore());

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('pagehide', this.handlePageHide);
      window.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('online', this.handleOnline);
    }
  }

  private handlePageHide = (): void => {
    this.disconnect();
  };

  private handleVisibilityChange = (): void => {
    if (typeof document === 'undefined') {
      return;
    }

    if (!document.hidden && !this.isConnected) {
      logger.info('[UserEventsSSEManager] Page became visible — reconnecting SSE if needed');
      this.syncWithStore();
    }
  };

  private handleOnline = (): void => {
    if (!this.isConnected) {
      // The network dropping out is the usual reason the budget ran out, so coming back online
      // is a new situation and earns a fresh one.
      this.resetReconnectState();
      logger.info('[UserEventsSSEManager] Network came back online — reconnecting SSE');
      this.syncWithStore();
    }
  };

  /**
   * Reads current store state and connects/disconnects SSE accordingly.
   * Called on every Redux state change.
   */
  private syncWithStore(): void {
    if (!this.store) {
      return;
    }

    const state = this.store.getState() as RootState;
    const isAuthenticated = hasValidCookie(state);
    // `setCookieSessionValid` notifies subscribers before the epic that builds the services layer
    // has run, so the AMS service is not there yet on the very first authenticated state change.
    const areServicesReady = state.app.isServicesReady;

    // A queued reconnect owns the next attempt - connecting here too would ignore the delay.
    // Once the attempt budget is spent nothing reconnects until the stream is healthy again,
    // the user signs out and back in, or the network comes back.
    if (isAuthenticated && areServicesReady && !this.isConnected && !this.reconnectTimer && !this.hasGivenUp) {
      this.connect();
    } else if (!isAuthenticated && this.isConnected) {
      this.disconnect();
    }
  }

  /**
   * Queues the next attempt. Idempotent, and always called before the dispatch that reports the
   * failure: that dispatch re-enters syncWithStore, which would otherwise reconnect immediately.
   * A failed stream reports both an error and a close, so both paths land here.
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.hasGivenUp) {
      return;
    }

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.hasGivenUp = true;
      logger.error(
        `[UserEventsSSEManager] Giving up - ${MAX_RECONNECT_ATTEMPTS} reconnect attempts all failed`,
      );
      return;
    }

    this.reconnectAttempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.syncWithStore();
    }, RECONNECT_DELAY_MS);
  }

  /** Gives the stream a fresh attempt budget, once it is known to work again. */
  private resetReconnectState(): void {
    this.reconnectAttempts = 0;
    this.hasGivenUp = false;
  }

  /** Refreshes the budget for a stream the server accepted and kept open. */
  private resetReconnectStateIfStreamWasHealthy(): void {
    if (this.streamOpenedAt !== null && Date.now() - this.streamOpenedAt >= HEALTHY_STREAM_MS) {
      this.resetReconnectState();
    }

    this.streamOpenedAt = null;
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Start SSE connection for the logged in user
   */
  public connect(): void {
    if (this.isConnected) {
      logger.warn('[UserEventsSSEManager] Already connected. Disconnect first before reconnecting.');
      return;
    }

    if (!GlobalService.amsService) {
      console.error('[UserEventsSSEManager] Cannot connect: AMS service is not created yet');
      return;
    }

    this.isConnected = true;

    if (this.store) {
      this.store.dispatch(userSSEConnectionStarted());
    }

    this.cleanupFn = GlobalService.amsService.streamUserEvents({
      onEvent: this.handleEvent.bind(this),
      onOpen: this.handleOpen.bind(this),
      onError: this.handleError.bind(this),
      onComplete: this.handleClose.bind(this),
    });
  }

  /**
   * Disconnect SSE connection
   */
  public disconnect(): void {
    this.cancelReconnect();
    this.resetReconnectState();
    this.streamOpenedAt = null;

    if (!this.isConnected) {
      return;
    }

    this.isIntentionalDisconnect = true;

    if (this.cleanupFn) {
      this.cleanupFn();
      this.cleanupFn = null;
    }

    this.isConnected = false;

    if (this.store) {
      this.store.dispatch(userSSEConnectionClosed());
    }
  }

  /**
   * Check if SSE is currently connected
   */
  public getIsConnected(): boolean {
    return this.isConnected;
  }

  /** The server accepted the stream - from here on it is judged on how long it lasts. */
  private handleOpen(): void {
    this.streamOpenedAt = Date.now();
  }

  /**
   * Handle incoming SSE events
   */
  private handleEvent(event: UserSSEEvent): void {
    console.warn('[UserEventsSSEManager] onEvent <-', event.type, '| id:', event.id, event.data);

    // Events only arrive over a working stream, so the previous failures no longer count.
    this.resetReconnectState();

    // Store the resume point before any filtering - the cursor is what we received, not what
    // we acted on, otherwise a skipped event would be replayed on every reconnect.
    setUserEventsLastEvent(event);

    this.consumeEvent(event);
  }

  /** Filtering and dispatching. */
  private consumeEvent(event: UserSSEEvent): void {
    // Filter out cash accounts - they are the ones created without a trading plan
    if (isAccountEvent(event) && !event.data.tradingPlanId) {
      console.warn('[UserEventsSSEManager] SKIPPED cash account (no trading plan):', event.type, event.data);
      return;
    }

    console.warn('[UserEventsSSEManager] HANDLED', event.type, '| id:', event.id, event.data);

    this.dispatchToStore(event);
  }

  /**
   * Dispatch event to Redux store
   */
  private dispatchToStore(event: UserSSEEvent): void {
    if (!this.store) {
      console.warn('[UserEventsSSEManager] Store not initialized. Cannot dispatch event.');
      return;
    }

    this.store.dispatch(userSSEEventReceived(event));
  }

  /**
   * Handle SSE errors
   */
  private handleError(error: Error): void {
    console.error('[UserEventsSSEManager] SSE Error:', error);

    // Every error reaching here is terminal - the stream stops, so it must not stay marked
    // as connected or syncWithStore would never reconnect it.
    this.isConnected = false;
    this.resetReconnectStateIfStreamWasHealthy();
    this.scheduleReconnect();

    if (this.store) {
      this.store.dispatch(userSSEConnectionError(error.message));
    }
  }

  /**
   * Handle SSE connection close
   */
  private handleClose(): void {
    if (this.isIntentionalDisconnect) {
      this.isIntentionalDisconnect = false;
      return;
    }

    this.isConnected = false;
    this.resetReconnectStateIfStreamWasHealthy();
    this.scheduleReconnect();

    if (this.store) {
      this.store.dispatch(userSSEConnectionClosed());
    }
  }
}

export default UserEventsSSEManager.getInstance();
